/** Due dates picked in a chosen time zone (not necessarily the browser's): "YYYY-MM-DDTHH:mm" in `zone` ↔ ISO. */

/** The browser's own zone, or UTC. */
export const browserZone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

/** Every zone the browser knows (a short list when it cannot say), with `extra` kept in. */
export function timeZones(extra: string): string[] {
  let all: string[] = [];
  try { all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? []; } catch { all = []; }
  if (!all.length) all = ['UTC', 'Europe/Helsinki', 'Europe/Stockholm', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo'];
  if (!all.includes('UTC')) all = ['UTC', ...all];
  return all.includes(extra) ? all : [extra, ...all];
}

/** The wall-clock parts of `ms` in `zone`. */
function partsIn(ms: number, zone: string) {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const p = Object.fromEntries(f.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: +p.year!, mo: +p.month!, d: +p.day!, h: +p.hour! % 24, mi: +p.minute!, s: +p.second! };
}
/** The zone's offset from UTC at `ms`, in milliseconds. */
function offsetAt(ms: number, zone: string): number {
  const p = partsIn(ms, zone);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}

/** "2026-11-20T16:00" in Europe/Helsinki → "2026-11-20T14:00:00.000Z". Empty → null. */
export function zonedToIso(local: string, zone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  const guess = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  let ms = guess - offsetAt(guess, zone);
  ms = guess - offsetAt(ms, zone);
  return new Date(ms).toISOString();
}

/** ISO → "YYYY-MM-DDTHH:mm" on the wall clock of `zone` (for a datetime-local input). */
export function isoToZoned(iso: string | null | undefined, zone: string): string {
  if (!iso) return '';
  const p = partsIn(new Date(iso).getTime(), zone);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${p.y}-${two(p.mo)}-${two(p.d)}T${two(p.h)}:${two(p.mi)}`;
}

/** A due date as staff read it: in their browser's zone. */
export function formatDue(iso: string | null): string {
  if (!iso) return 'No due date';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}
